/**
 * The Elevate rail's pool-work lens (#1000).
 *
 * Queue membership is not authority and a queue assignment is not visibility.
 * The server therefore returns only the queues whose team the caller belongs to
 * and counts only rows the caller can already read. This panel renders that
 * answer; it never queries host tables or reconstructs a cross-entity queue in
 * the browser. Picking up happens beside an already-governed row in ListView.
 */
import type { WorkQueueSummary } from '../data/queries'
import { useWorkQueues } from '../data/queries'
import { ErrorState, SkeletonText } from '@solum/ui'
import { DrawerGrid, type DrawerColumn } from './DrawerRows'

const COLUMNS: DrawerColumn[] = [
  { key: 'queue', label: 'Queue', width: 'minmax(0,1fr)' },
  { key: 'open', label: 'Open', width: 'auto', align: 'right' },
]

export function WorkQueueList({ queues }: { queues: WorkQueueSummary[] }) {
  return (
    <DrawerGrid
      columns={COLUMNS}
      title="Work queues"
      itemsLabel="queues"
      testId="work-queues-grid"
      empty="You do not belong to an active work queue."
      rows={queues.map((queue) => ({
        key: queue.id,
        label: `${queue.name} — ${queue.open_items} claimable item${queue.open_items === 1 ? '' : 's'}`,
        cells: [queue.name, <span className="tnum" key="count">{queue.open_items}</span>],
        detail: queue.open_items > 0 ? 'Visible queue-assigned rows offer Pick up in their module list.' : 'Nothing visible is waiting in this queue.',
        tone: queue.open_items > 0 ? undefined : 'muted',
      }))}
    />
  )
}

export function WorkQueuesPanel() {
  const queues = useWorkQueues()
  /* The success render is a `DrawerGrid`, so the waiting state is a skeleton
     of the rows that are coming, not a sentence about waiting. Both arms used
     to be a muted `<div>` — the same treatment for "still loading" and "this
     failed", which are not the same thing to a reader. */
  if (queues.isLoading) return <SkeletonText lines={3} />
  if (queues.isError) {
    return <ErrorState title="Could not read your work queues" message={(queues.error as Error).message} />
  }
  return <WorkQueueList queues={queues.data ?? []} />
}
